/**
 * Flow tag overlay for the preview — fetches the ranking tag rules and computes
 * each item's tags client-side from the in-memory preview items (rawScore +
 * metrics are already there), so chips appear in the optimizer preview with no
 * extra backend call. Thresholds resolve against the campaign + country the
 * preview response carries, so the chips match the server's own verdict:
 * campaign override, else country, else the condition's default. An unlinked or
 * shared campaign resolves to '' and falls back to the default.
 * For Media Devoted | Created By Ciro Autiero
 */

import { createContext, useContext, useMemo, type FC, type ReactNode } from 'react';
import {
  evaluateFlowTags,
  flowTagItemKey,
  type FlowLayer,
  type FlowOptimizerItemPreview,
  type FlowTagResult,
  type FlowTagRule,
  type TagTrafficProvider,
} from '@trafficpulse/shared';

import { useFlowTagRules } from '@/hooks';

export interface FlowTagsLookup {
  tagsFor: (layer: FlowLayer, itemId: string, pathId?: string) => string[];
  colorOf: (tag: string) => string | undefined;
  emojiOf: (tag: string) => string | undefined;
}

const FlowTagsContext = createContext<FlowTagsLookup | null>(null);

/**
 * Rebuild the PRE-EXCLUSION snapshot the server tagged (context-builder's
 * preExclusionTagItem): rules run BEFORE exclusions, so an item excluded BY a
 * rule was eligible — and tagged — when the server evaluated. The preview rows
 * carry the post-decision state, which diverges from that snapshot two ways:
 *
 * - Flags: feeding final rows to evaluateFlowTags made isEligible skip exactly
 *   the rows a tag condition excluded — the hover said `tagged TEST` while no
 *   row showed a TEST chip — and shrank the ranking pools under the survivors.
 *   Rule-excluded rows re-enter; inactive / no-metrics rows (excluded without
 *   ruleExcluded) stay out, like the server's snapshot.
 * - Ranking metrics: shares aren't allocated yet at rule time, so the server
 *   seeds scoreShare/optimalWeight with the clamped rawScore as the ordering
 *   proxy. The final rows instead carry POST-allocation values — 0 share on an
 *   excluded row, solver-clamped weights elsewhere — so "Rank by score share /
 *   optimal weight" chips would land on different rows than the server's
 *   verdict. Re-seed both from rawScore; ranking compares order only.
 */
export function toPreExclusionItems(
  items: ReadonlyArray<FlowOptimizerItemPreview>,
): ReadonlyArray<FlowOptimizerItemPreview> {
  return items.map((it) => {
    const rawScore = Math.max(0, it.rawScore ?? 0);
    return {
      ...it,
      rawScore,
      scoreShare: rawScore,
      optimalWeight: rawScore,
      excluded: it.ruleExcluded ? false : it.excluded,
      ruleExcluded: false,
    };
  });
}

export const FlowTagsProvider: FC<{
  items: ReadonlyArray<FlowOptimizerItemPreview>;
  /** SMSWiser country of the previewed campaign; '' when unknown. */
  countryKey?: string;
  /** Previewed campaign + its tracker — together they select campaign overrides. */
  campaignId?: string;
  provider?: TagTrafficProvider;
  children: ReactNode;
}> = ({ items, countryKey = '', campaignId, provider, children }) => {
  const paths = useFlowTagRules('paths');
  const landers = useFlowTagRules('landers');
  const offers = useFlowTagRules('offers');

  const rules = useMemo<FlowTagRule[]>(
    () => [...(paths.data ?? []), ...(landers.data ?? []), ...(offers.data ?? [])],
    [paths.data, landers.data, offers.data],
  );

  const value = useMemo<FlowTagsLookup>(() => {
    // Defensive: tolerate a partial result (e.g. an older shared build without
    // tagEmojis) so a missing map can never throw on lookup.
    const result = evaluateFlowTags(toPreExclusionItems(items), rules, {
      countryKey,
      campaignId,
      provider,
    }) as Partial<FlowTagResult>;
    const tagsByKey = result.tagsByKey ?? {};
    const tagColors = result.tagColors ?? {};
    const tagEmojis = result.tagEmojis ?? {};
    return {
      tagsFor: (layer, itemId, pathId) =>
        tagsByKey[flowTagItemKey(layer, itemId, pathId)] ?? [],
      colorOf: (tag) => tagColors[tag],
      emojiOf: (tag) => tagEmojis[tag],
    };
  }, [items, rules, countryKey, campaignId, provider]);

  return <FlowTagsContext.Provider value={value}>{children}</FlowTagsContext.Provider>;
};

export function useFlowTagsFor(
  layer: FlowLayer,
  itemId: string,
  pathId?: string,
): {
  tags: string[];
  colorOf: (tag: string) => string | undefined;
  emojiOf: (tag: string) => string | undefined;
} {
  const ctx = useContext(FlowTagsContext);
  if (!ctx) return { tags: [], colorOf: () => undefined, emojiOf: () => undefined };
  return {
    tags: ctx.tagsFor(layer, itemId, pathId),
    colorOf: ctx.colorOf,
    emojiOf: ctx.emojiOf,
  };
}
